import { useReducer } from 'react';

import {
  getIntentLink,
  IntentPayload,
  sendIntent,
  sendIntentWithResponse,
  type SendIntentOptions,
  type SendIntentWithResponseOptions,
} from '@dynatrace-sdk/navigation';

function getAppIconURL(appId?: string) {
  if (!appId) {
    return null;
  }
  return `/platform/app-engine/registry/v1/app-icons/${appId}`;
}

/**
 * @internal
 */
export interface IntentParams {
  payload: IntentPayload;
  options?: SendIntentOptions<IntentPayload>;
}

interface UseIntentBaseReturn {
  /** A static intent link, generated with `getIntentLink()` */
  link: string;
  iconURL: string | null;
}

/**
 * @internal
 */
export interface UseIntentReturn extends UseIntentBaseReturn {
  send: () => void;
}

/**
 * Wrapper around `@dynatrace-sdk/navigation`s `sendIntent()` method for easier integration with react.
 * @internal
 */
export function useIntent({ payload, options }: IntentParams): UseIntentReturn {
  const link = getIntentLink(payload);
  let iconURL: string | null = null;
  if (options?.recommendedAppId) {
    iconURL = getAppIconURL(options.recommendedAppId);
  }

  function send() {
    if (!options) {
      return sendIntent(payload);
    }
    return sendIntent(payload, options);
  }

  return { send, link, iconURL };
}

interface IntentIdleState {
  status: 'idle';
}

interface IntentPendingState {
  status: 'pending';
}

interface IntentSuccessState {
  status: 'success';
  data: Record<string, unknown>;
}

interface IntentErrorState {
  status: 'error';
  error: Error;
}

type IntentState =
  | IntentIdleState
  | IntentPendingState
  | IntentSuccessState
  | IntentErrorState;

type ACTIONTYPE =
  | { type: 'send' }
  | { type: 'success'; payload: IntentSuccessState['data'] }
  | { type: 'error'; payload: Error };

function reducer(_: IntentState, action: ACTIONTYPE): IntentState {
  switch (action.type) {
    case 'send':
      return { status: 'pending' };
    case 'success':
      return { status: 'success', data: action.payload };
    case 'error':
      return { status: 'error', error: action.payload };
    default:
      throw new Error();
  }
}

/**
 * @internal
 */
export interface IntentWithResponseParams {
  payload: IntentPayload;
  options: SendIntentWithResponseOptions<string>;
}

/**
 * @internal
 */
export interface UseIntentWithResponseReturn extends UseIntentBaseReturn {
  send: () => Promise<IntentSuccessState | IntentErrorState>;
  /** The current state of the `sendIntentWithResponse()` call. */
  state: IntentState;
}

/**
 * Wrapper around `@dynatrace-sdk/navigation`s `sendIntentWithResponse()` method for easier integration with react.
 * @internal
 */
export function useIntentWithResponse({
  payload,
  options,
}: IntentWithResponseParams): UseIntentWithResponseReturn {
  const link = getIntentLink(payload);
  const iconURL = getAppIconURL(options.recommendedAppId);

  const [state, dispatch] = useReducer(reducer, {
    status: 'idle',
  });

  async function send(
    overridePayload?: IntentPayload,
  ): Promise<IntentSuccessState | IntentErrorState> {
    try {
      dispatch({ type: 'send' });
      const res = await sendIntentWithResponse(
        overridePayload || payload,
        options,
      );

      /* Check for ^1.0.0 or ^2.0.0 response and handle the resulting response accordingly.

      V1:
      Throws an error if something went wrong
      Return Type
      export type Response<T extends string> = {
          response: {
              [key in T]: unknown;
          };
      };

      V2:
      Has errors in return types:
      export interface IntentResponseErrorEnvelope {
          ok: false;
          error: IntentError;
      }
      export interface IntentResponseOkEnvelope<T extends string = ''> {
          ok: true;
          data: {
              [key in T]: unknown;
          };
      }
      export type IntentResponseEnvelope<T extends string> = IntentResponseOkEnvelope<T> | IntentResponseErrorEnvelope;
      */

      if (!res) {
        const error = new Error('No response received');
        dispatch({
          type: 'error',
          payload: error,
        });
        return {
          status: 'error',
          error,
        };
      }

      const isV2 = 'ok' in res;
      if (isV2) {
        if (res.ok) {
          dispatch({ type: 'success', payload: { response: res?.data } });
          return { status: 'success', data: { response: res?.data } };
        } else {
          const error = new Error(`Intent Error: ${res.error.reason}`);
          dispatch({
            type: 'error',
            payload: error,
          });
          return {
            status: 'error',
            error,
          };
        }
      } else {
        // we do not need additional error handling for v1, since it will throw and run in the catch below.
        // we need to disable the typing here, since we use v2 ourselves and v1 has the 'response' property instead of 'data'.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        dispatch({ type: 'success', payload: { response: res.response } });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        return { status: 'success', data: { response: res.response } };
      }
    } catch {
      const error = new Error('Error sending intent with response');
      dispatch({
        type: 'error',
        payload: error,
      });
      return {
        status: 'error',
        error,
      };
    }
  }

  return { send, state, link, iconURL };
}
